<template>
    <div class="dash-programme-modules">
      <k-nav-tabs :tabs=tabs
                  :current-tab="tab"
                  :useRoutes="false"
                  @tab-selected="tab = $event">
      </k-nav-tabs>
      <span v-if="message">{{ message }}</span>
      <div v-if="showModules">
        <k-loader v-if="!modulesReady" class="k-loader"/>
        <div v-else>
          <div v-for="mod in trackedModules" :key="mod.id">
            <learner-module-card
              :programme-id="programmeId"
              :is-learner-support="isLearnerSupport"
              :programme-module="mod"
              :programme-member-id="programmeMemberId"
              @refresh-modules="getModules"
              >
            </learner-module-card>
          </div>
        </div>
      </div>
      <div v-if="showAchievements">
        <k-loader v-if="!badgesReady && !certificatesReady" class="k-loader"/>
        <div v-else>
          <div v-if="badges.length">
            <span class="badges-header">
              <h3>Badges</h3>
            </span>
            <k-progress class="badgeProgress"
                        :percentage="percentOfBadgesAchieved"
                        :customText="' of badges awarded'"
                        ariaLabel="Percentage of badges achieved"
                        :precision="2"></k-progress>
            <div v-if="revokingAchievement.badge || !badgesReady" class="revoking-achievement badges">
              <k-loader class="k-loader"/>
            </div>
            <div v-else>
              <achievement-award-card v-for="badge in orderedBadges"
                                      :key="badge.id"
                                      :achievement="badge"
                                      :revocable="true"
                                      @revoke-achievement="confirmRevokeAchievement"></achievement-award-card>
            </div>
          </div>
          <div v-if="programmeCertificate">
            <span class="certificate-header">
              <h3>Certificate</h3>
              <button class="btn btn-primary award-certificate-button"
                      @click="confirmCertificateAward()"
                      v-if="!certificateAwarded && this.$permissions.hasPermission('award_programme_certificate')">Award Certificate</button>
            </span>
            <div v-if="revokingAchievement.certificate || !certificatesReady" class="revoking-achievement certificate">
              <k-loader class="k-loader"/>
            </div>
            <div v-else>
              <achievement-award-card
                :achievement="programmeCertificate"
                :revocable="true"
                :achievement-type="'certificate'"
                @revoke-achievement="confirmRevokeAchievement"
              ></achievement-award-card>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showCompetencies">
        <k-loader v-if="!competenciesReady" class="k-loader"/>
        <template v-else>
          <tab-controls
            current-tab="programme_competencies"
            :sort-mode="competencySortMode"
            :show-layout-toggle="false"
            @change-sort-mode="changeCompetencySortMode"
            @filter="filterCompetencies"
          />
          <programme-competencies
            :competencies="filteredCompetencies"
            :has-programme-competencies="competencies.length > 0"
            @save-competency="saveCompetency"
          />
        </template>
      </div>
      <div v-if="showOtj">
        <dashboard-otj-logs
          :programme-id="programmeId"
          :user-id="studentId"
          :programme-member-id="programmeMemberId"
        ></dashboard-otj-logs>
      </div>
      <div v-if="showMeetings">
        <meetings-tab :programme-id="programmeId" :user-id="studentId"></meetings-tab>
      </div>
      <div v-if="showProjects">
        <projects-tab :programme-id="programmeId" :user-id="studentId"></projects-tab>
      </div>
    </div>
</template>

<style scoped>
  .dash-programme-modules :deep(.actions-container) {
    margin-bottom: 15px;
    justify-content: flex-end;
  }

  .badgeProgress {
    margin-bottom: 20px;
  }

  .badges-header > h3 {
    margin-bottom: 20px;
  }

  .certificate-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .revoking-achievement {
    display: flex;
    justify-content: center;
  }

  .k-loader {
    display: flex;
    justify-content: center;
  }
</style>

<script>
import PageReadyMixin from '@mixins/page-ready-mixin';
import TimeMixin from '@mixins/time-mixins';
import KNavTabs from '@base-components/k-nav-tabs.vue';
import KProgress from '@base-components/k-progress.vue';
import KLoader from '@base-components/k-loader.vue';
import { sortObjectArray } from '@utils/sort-by-object-property';
import { COMPETENCY_SORT_MODES, sortCompetencies, filterCompetencyList } from '@utils/competency-list';
import TabControls from '../../../programmes/components/programme-content-tab-controls.vue';
import DashboardModuleCard from './dashboard-module-card.vue';
import AchievementAwardCard from '../../achievements/achievement-award-card.vue';
import ProgrammeCompetencies from './programme-competencies.vue';
import DashboardOtjLogs from './dashboard-otj-logs.vue';
import MeetingsTab from './meetings-tab.vue';
import ProjectsTab from './projects-tab.vue';

const AVAILABLE_TABS = [
  { key: 'curriculum', name: 'Curriculum' },
  { key: 'milestone', name: 'Milestones' },
  { key: 'achievements', name: 'Achievements', permission: 'view_user_achievements' },
  { key: 'competencies', name: 'Competencies', permission: 'view_user_competencies' },
  { key: 'otj', name: 'O.T.J. Logs', permission: 'view_user_otjs' },
  { key: 'meetings', name: 'Meetings', permission: 'view_progress_reviews' },
  {
    key: 'projects', name: 'Projects', permission: 'view_user_projects', featureFlag: 'kate-projects-dashboard',
  },
];

export default {
  components: {
    MeetingsTab,
    ProjectsTab,
    KProgress,
    KNavTabs,
    KLoader,
    TabControls,
    AchievementAwardCard,
    'learner-module-card': DashboardModuleCard,
    ProgrammeCompetencies,
    DashboardOtjLogs,
  },

  props: {
    programmeId: Number,
    programmeName: String,
    programmeMemberId: Number,
    isLearnerSupport: Boolean,
    studentId: Number,
  },

  mixins: [PageReadyMixin, TimeMixin],

  data() {
    return {
      modules: [],
      modulesReady: false,
      badges: [],
      badgesReady: false,
      programmeCertificate: undefined,
      revokingAchievement: { badge: false, certificate: false },
      certificatesReady: false,
      competenciesReady: false,
      competencies: [],
      tab: 'curriculum',
      competencySortMode: COMPETENCY_SORT_MODES.DEFAULT,
      filterText: '',
      categoryFilterFunc: () => true,
    };
  },

  mounted() {
    this.getProgrammeData();
  },

  watch: {
    programmeId() {
      this.getProgrammeData();
    },
  },

  computed: {
    tabs() {
      const tabs = AVAILABLE_TABS.filter(tab => {
        const hasPermission = tab.permission ? this.$permissions.hasPermission(tab.permission) : true;
        const hasFeatureFlag = tab.featureFlag ? this.$featureFlags.isEnabled(tab.featureFlag) : true;
        return hasPermission && hasFeatureFlag;
      }).map(tab => ({ key: tab.key, name: tab.name }));
      return tabs;
    },
    ready() {
      return this.modulesReady && this.badgesReady && this.certificatesReady && this.competenciesReady;
    },
    trackedModules() {
      return this.modules.filter(x => x.track === this.tab);
    },
    orderedBadges() {
      return sortObjectArray(this.badges, 'date_issued', true);
    },
    percentOfBadgesAchieved() {
      if (!this.badges?.length) {
        return 0;
      }
      return (this.badges.filter(x => x.has_been_awarded).length / this.badges.length) * 100;
    },
    showModules() {
      return this.tab === 'curriculum' || this.tab === 'milestone';
    },
    showAchievements() {
      return this.tab === 'achievements';
    },
    showCompetencies() {
      return this.tab === 'competencies';
    },
    showOtj() {
      return this.tab === 'otj';
    },
    showMeetings() {
      return this.tab === 'meetings';
    },
    showProjects() {
      return this.tab === 'projects';
    },
    message() {
      if (this.modulesReady && this.showModules && !this.trackedModules.length) {
        return `There are no ${this.tab} modules in this programme`;
      }
      if (this.badgesReady && this.showAchievements && !this.badges?.length && !this.programmeCertificate) {
        return 'There are no achievements to be awarded in this programme';
      }
      return undefined;
    },
    certificateAwarded() {
      return this.programmeCertificate?.has_been_awarded;
    },
    sortedCompetencies() {
      return sortCompetencies(this.competencies, this.competencySortMode);
    },
    filteredCompetencies() {
      return filterCompetencyList(this.sortedCompetencies, {
        text: this.filterText,
        categoryFilter: this.categoryFilterFunc,
        parseTimestamp: this.parseTimestamp,
      });
    },
  },

  methods: {
    getProgrammeData() {
      // In a future task move each of them to a different component
      // Not an overkill but it will maintain consistency and better practice
      this.getModules();
      this.getBadges();
      this.getCertificates();
      this.getCompetencies();
    },
    getModules() {
      this.$logger.info('Getting modules for programme', { progId: this.programmeId }, true);
      this.modulesReady = false;
      this.modules = [];
      this.$http.get(`/api/curriculum/admin/programmes/${this.programmeId}/user/${this.studentId}/modules`).then(res => {
        this.modules = sortObjectArray(res.data.modules, 'number');
        this.originalModules = sortObjectArray(JSON.parse(JSON.stringify(res.data.modules)), 'number');
        this.$logger.info('Got modules for programme', { progId: this.programmeId });
      }).catch(err => {
        this.$logger.error('Error getting modules for programme', { progId: this.programmeId }, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.modulesReady = true;
      });
    },
    getBadges() {
      this.$logger.info('Getting badges for user in programme', { progId: this.programmeId, userId: this.studentId }, true);
      this.badgesReady = false;
      this.badges = [];
      this.$http.get(`/api/curriculum/admin/programmes/${this.programmeId}/badges/user/${this.studentId}`).then(res => {
        this.badges = res.data;
        this.$logger.info('Got badges for user in programme', { progId: this.programmeId, userId: this.studentId });
      }).catch(err => {
        this.$logger.error('Error getting badges for user in programme', { progId: this.programmeId, userId: this.studentId }, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.badgesReady = true;
      });
    },
    getCertificates() {
      this.$logger.info('Getting certificate for user in programme', { progId: this.programmeId, userId: this.studentId }, true);
      this.certificatesReady = false;
      this.programmeCertificate = undefined;
      this.$http.get(`/api/curriculum/admin/programmes/${this.programmeId}/certificates/user/${this.studentId}`).then(res => {
        this.programmeCertificate = res.data.find(c => c.programme_id === this.programmeId);
        this.$logger.info('Got certificate for user in programme', { progId: this.programmeId, userId: this.studentId });
      }).catch(err => {
        this.$logger.autowarn('Error getting certificate for user in programme', { progId: this.programmeId, userId: this.studentId }, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.certificatesReady = true;
      });
    },

    getCompetencies() {
      this.$logger.info('Getting competencies for user in programme', { progId: this.programmeId, userId: this.studentId }, true);
      this.competenciesReady = false;
      this.competencies = [];
      this.$http.get(`/api/curriculum/admin/programmes/${this.programmeId}/user/${this.studentId}/competencies/scores`).then(res => {
        this.competencies = res.data.competencies;
        this.$logger.info('Got competencies for user in programme', { progId: this.programmeId, userId: this.studentId });
      }).catch(err => {
        this.$logger.autowarn('Error getting competencies for user in programme', { progId: this.programmeId, userId: this.studentId }, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.competenciesReady = true;
      });
    },

    toastMessage() {
      return 'You are about to award this certificate. The learner will be able to view it in their profile and share '
          + 'it publicly.';
    },

    confirmCertificateAward() {
      this.$ktoast.confirmToast(this.toastMessage(), 'warning', this.awardCertificate);
    },

    awardCertificate() {
      this.$Loading.start();
      const payload = {
        programme_id: this.programmeId,
        user_id: this.studentId,
      };
      this.$logger.info('Awarding certificate', { programmeId: this.programmeId }, true);
      this.$http.put('/api/curriculum/programmes/certificates/user', payload).then(() => {
        this.$logger.info('Awarded certificate', { programmeId: this.programmeId });
        this.getCertificates();
      }).catch(err => {
        this.$ktoast.showError(err);
        this.$logger.autowarn('Error awarding certificate manually', { programmeId: this.programmeId }, err);
      }).then(() => {
        this.$Loading.finish();
      });
    },

    revokeAchievement(achievementId, achievementType) {
      let endpoint;
      this.revokingAchievement[achievementType] = true;
      if (achievementType === 'badge') {
        endpoint = `/api/curriculum/programme_badge/${achievementId}/user/${this.studentId}`;
      } else {
        endpoint = `/api/curriculum/programme/${achievementId}/user/${this.studentId}/certificate`;
      }
      this.$logger.info(`Revoking ${achievementType}`, { achievementId, studentId: this.studentId });
      this.$http.delete(endpoint).then(() => {
        this.$logger.info(`Revoked ${achievementType}`, { achievementId, studentId: this.studentId });
        if (achievementType === 'badge') {
          this.getBadges();
        } else {
          this.getCertificates();
        }
      }).catch(err => {
        this.$ktoast.showError(err);
        this.$logger.autowarn(`Error revoking ${achievementType}`, { achievementId, studentId: this.studentId }, err);
      }).then(() => {
        this.revokingAchievement[achievementType] = false;
      });
    },

    confirmRevokeAchievement(achievementId, achievementType) {
      this.$ktoast.confirmToast(`You are about to revoke this ${achievementType}.`, 'warning', () => {
        this.revokeAchievement(achievementId, achievementType);
      });
    },

    saveCompetency(competencyId, score) {
      this.$logger.info('Saving competency for the learner', { competencyId, score }, true);
      const payload = {
        score,
        programme_member_id: this.programmeMemberId,
      };
      this.$http.put(`/api/curriculum/admin/programmes/user/${this.studentId}/competencies/${competencyId}/scores`, payload)
        .then(res => {
          this.$logger.info('Competency saved', res.data, true);
          this.getCompetencies();
        })
        .catch(error => {
          this.$logger.error('Error saving the competency', undefined, error);
        });
    },

    changeCompetencySortMode(mode) {
      this.competencySortMode = mode;
    },

    filterCompetencies({ text, categoryFilter }) {
      this.filterText = text;
      this.categoryFilterFunc = categoryFilter || (() => true);
    },
  },
};
</script>
